import { useEffect, useRef, useState } from "react";
import { postMutation } from "../../api";
import type { AskRecordPage, AskRecordRow, AskResultSet } from "../../../../../backend/src/shared/askRecordTools";
import { C } from "../../tokens";
import type { CitationTarget } from "./citationTarget";

const kindLabels = { task: "Task", observation: "Observation", dailyLog: "Daily log", punch: "Punch item", walk: "Walk", photo: "Photo" };
const pluralKindLabels = { task: "tasks", observation: "observations", dailyLog: "daily logs", punch: "punch items", walk: "walks", photo: "photos" };
// "tasks, observations, and punch items", not "tasks and observations and punch items".
const kindList = new Intl.ListFormat("en-US", { style: "long", type: "conjunction" });

export function AskMatchingRecords({ resultSets, search, onOpen, hrefFor }: {
  resultSets?: AskResultSet[];
  search: string;
  onOpen: (target: CitationTarget) => void;
  hrefFor: (target: CitationTarget) => string;
}) {
  // A multi-query answer does not identify which full set supports its final
  // conclusion. Do not guess, or expose the intermediate searches as sources.
  const set = resultSets?.length === 1 ? resultSets[0] : undefined;
  const kindNames = set?.query.kinds?.map(kind => set.total === 1 ? kindLabels[kind].toLowerCase() : pluralKindLabels[kind]);
  const kinds = kindNames?.length ? kindList.format(kindNames) : "";
  const label = set?.label && !/^Matching records(?: by .+)?$/i.test(set.label)
    ? set.label
    : set && (kinds || set.query.status) ? [set.query.status, kinds || "records"].filter(Boolean).join(" ") : "";
  return <>
    {set && set.total > 0 && label && <MatchingSet key={`${JSON.stringify(set.query)}:${search}`} set={set} label={label} search={search} onOpen={onOpen} hrefFor={hrefFor} />}
    {resultSets?.some(result => result.coverage === "recorded_assignments_only") && <div data-testid="assignment-coverage" style={{ color: C.sub2, fontSize: 14, marginTop: 7 }}>
      Only assignments with recorded history are counted. Older or imported assignments without a recorded actor are excluded.
    </div>}
  </>;
}

function MatchingSet({ set, label, search, onOpen, hrefFor }: {
  set: AskResultSet; label: string; search: string; onOpen: (target: CitationTarget) => void; hrefFor: (target: CitationTarget) => string;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<AskRecordPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  async function load(offset: number) {
    setLoading(true); setError(null);
    const response = await postMutation<AskRecordPage>("/api/ask/records", search, { filters: set.query, projectIds: set.projectIds, offset, limit: 25, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
    if (!alive.current) return;
    setLoading(false);
    if (!response.ok || !response.result) setError(response.error ?? "Matching records could not be loaded.");
    else setPage(response.result);
  }
  function target(row: AskRecordRow): CitationTarget | null {
    return row.kind === "walk" || row.kind === "photo" || row.citationKind === "walk"
      ? row.visitId ? { target: "walk", id: row.visitId } : null
      : { target: "item", id: row.id };
  }
  const buttonStyle = { border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 12px", background: C.surface, color: C.ink, cursor: "pointer", fontSize: 14 };
  return <div data-testid="ask-matching-records" style={{ fontSize: 14, lineHeight: 1.5 }}>
    <button type="button" className="kb" aria-expanded={open} style={buttonStyle} onClick={() => { setOpen(!open); if (!open && !page && !loading) void load(0); }}>
      {open ? `Hide ${label}` : `See all ${set.total.toLocaleString("en-US")} ${label}`}
    </button>
    {open && <div style={{ marginTop: 12, border: `1px solid ${C.line}`, borderRadius: 10, padding: 12 }}>
      <strong>{label}</strong>
      <div style={{ color: C.sub2, fontSize: 12 }}>Live results in this workspace · {set.query.kinds?.map((kind) => kindLabels[kind]).join(", ")}{set.query.status ? ` · ${set.query.status}` : ""}{set.query.project ? ` · ${set.query.project}` : ""}</div>
      {loading && <div role="status">Loading matching records…</div>}
      {error && <div role="alert" style={{ marginTop: 8 }}>{error} <button type="button" style={buttonStyle} onClick={() => void load(page?.offset ?? 0)}>Retry</button></div>}
      {page && <>
        <div style={{ color: C.sub2, marginTop: 8 }}>{page.total ? `${page.offset + 1}–${page.offset + page.rows.length} of ${page.total.toLocaleString("en-US")}` : "No matching records now."}</div>
        <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", maxHeight: 420, overflowY: "auto" }}>{page.rows.map((row) => {
          const destination = target(row);
          return <li key={`${row.kind}:${row.id}`} style={{ borderTop: `1px solid ${C.line}`, padding: "10px 0", overflowWrap: "anywhere" }}>
            {destination ? <a href={hrefFor(destination)} onClick={(event) => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onOpen(destination); } }} style={{ color: C.ink, textDecoration: "underline" }}>{row.title}</a> : row.title}
            <div style={{ color: C.sub2, fontSize: 12 }}>{kindLabels[row.kind]} · {row.siteName}{row.assignee ? ` · ${row.assignee}` : ""}{row.status ? ` · ${row.status.replace(/_/g, " ")}` : ""}</div>
          </li>;
        })}</ul>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" style={buttonStyle} disabled={loading || page.offset === 0} onClick={() => void load(Math.max(0, page.offset - 25))}>Previous</button>
          <button type="button" style={buttonStyle} disabled={loading || page.nextOffset === null} onClick={() => void load(page.nextOffset!)}>Next</button>
        </div>
      </>}
    </div>}
  </div>;
}
